import { ArrowsMove } from "@styled-icons/bootstrap";
import { StyledIconBase } from "@styled-icons/styled-icon";
import { Add, HeartFilled, Remove, Reset, TextToSpeech, WidgetSettings } from "assets/icons/Common";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { lerp } from "components/utils";
import { useTTS } from "hooks/tts";
import { createRef, useEffect, useRef, useState } from "react";
import Draggable, { DraggableEvent } from "react-draggable";
import { DragCallbackData } from "react-resizable";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";
import { elementsOverlap } from "util/elementsOverlay";

import { PhonemeData, PhonemeGroups, Phonemes } from "./phonemes";

export interface WordBuilderState {
  soundSlots: SoundSlot[];
  heartSlots: number[];
}

const MaxSlots = 11;

export interface SoundSlot {
  value: string;
  isDisabled?: boolean;
  relatedSoundIndex?: number;
}

export const WordBuilder: WidgetFC<WordBuilderState> = ({
  state: { soundSlots, heartSlots },
  settings,
  onOpenSettings,
  setState,
}) => {
  const { phonemesDisabled, heartParts } = settings;
  const dropZoneRefs = useRef(soundSlots.map(() => createRef<HTMLDivElement>()));
  const heartZoneRefs = useRef(soundSlots.map(() => createRef<HTMLDivElement>()));
  const [currentHoverDropzone, setCurrentHoverDropzone] = useState<number | undefined>();
  const [currentHoverHeart, setCurrentHoverHeart] = useState<number | undefined>();
  const [currentDragPhoneme, setCurrentDragPhoneme] = useState<PhonemeData | undefined>();
  const { readAloud } = useTTS();
  const groupsToShow = Object.keys(PhonemeGroups)
    .filter(
      (groupKey) =>
        PhonemeGroups[groupKey].groups.filter((groupKey) => !phonemesDisabled[groupKey].disabled)
          .length > 0,
    )
    .filter(
      (groupKey) =>
        PhonemeGroups[groupKey].groups.filter(
          (key) => Phonemes[key].phonemes.length !== phonemesDisabled[key].disabledSounds.length,
        ).length > 0,
    );

  const setSoundSlots = (soundSlots: SoundSlot[]) => {
    setState({ soundSlots });
  };

  const setHeartSlots = (heartSlots: number[]) => {
    setState({ heartSlots });
  };

  const setAllState = (soundSlots: SoundSlot[], heartSlots: number[]) => {
    setState({
      soundSlots,
      heartSlots,
    });
  };

  useEffect(() => {
    if (dropZoneRefs.current) {
      dropZoneRefs.current = soundSlots.map(() => createRef<HTMLDivElement>());
    }
    if (heartZoneRefs.current) {
      heartZoneRefs.current = soundSlots.map(() => createRef<HTMLDivElement>());
    }

    const newHeartSlots = [...heartSlots];
    for (let i = 0; i < newHeartSlots.length; i++) {
      if (heartSlots[i] >= soundSlots.length) {
        newHeartSlots.splice(i, 1);
        i--;
      }
    }
    setHeartSlots(newHeartSlots);
  }, [soundSlots]);

  const onDragPhonemeStop = (e: DraggableEvent, data: DragCallbackData) => {
    if (currentHoverDropzone !== undefined && currentDragPhoneme !== undefined) {
      const newSoundSlots = [...soundSlots];

      if (currentDragPhoneme.sounds.length === 1) {
        newSoundSlots[currentHoverDropzone].value = currentDragPhoneme.sounds[0];
      } else {
        const width = currentDragPhoneme.sounds.length;
        let startIndex = currentHoverDropzone;
        let endIndex = currentHoverDropzone + width;
        const requiredSlots = currentHoverDropzone + width;

        if (requiredSlots < MaxSlots) {
          if (requiredSlots >= soundSlots.length) {
            while (newSoundSlots.length < requiredSlots) {
              newSoundSlots.push({ value: "" });
            }
          }
          if (currentDragPhoneme.sounds.length === 2) {
            newSoundSlots[startIndex] = {
              value: currentDragPhoneme.sounds[0],
              relatedSoundIndex: endIndex,
            };
            newSoundSlots[endIndex] = {
              value: currentDragPhoneme.sounds[1],
              isDisabled: endIndex >= soundSlots.length,
            };

            for (let i = startIndex + 1; i < endIndex; i++) {
              newSoundSlots[i].value = "";
            }
          }
        }
      }

      setSoundSlots(newSoundSlots);
    }

    setCurrentDragPhoneme(undefined);
    setCurrentHoverDropzone(undefined);
  };

  const onDragPhoneme = (e: DraggableEvent, data: DragCallbackData) => {
    let anyHover = false;
    for (let i = 0; i < dropZoneRefs.current.length; i++) {
      if (
        dropZoneRefs.current[i].current &&
        elementsOverlap(data.node, dropZoneRefs.current[i].current!)
      ) {
        setCurrentHoverDropzone(i);
        anyHover = true;
        break;
      }
    }

    if (!anyHover) {
      setCurrentHoverDropzone(undefined);
    }
  };

  const onDragPhonemeStart = (e: DraggableEvent, data: DragCallbackData, phoneme: PhonemeData) => {
    setCurrentDragPhoneme(phoneme);
  };

  const onDragHeartStop = (e: DraggableEvent, data: DragCallbackData) => {
    if (currentHoverHeart !== undefined) {
      const newHeartSlots = [...heartSlots];
      if (!newHeartSlots.includes(currentHoverHeart)) {
        newHeartSlots.push(currentHoverHeart);
      }
      setHeartSlots(newHeartSlots);
    }

    setCurrentHoverHeart(undefined);
  };

  const onDragHeart = (e: DraggableEvent, data: DragCallbackData) => {
    let anyHover = false;
    for (let i = 0; i < heartZoneRefs.current.length; i++) {
      if (
        heartZoneRefs.current[i].current &&
        elementsOverlap(data.node, heartZoneRefs.current[i].current!)
      ) {
        setCurrentHoverHeart(i);
        anyHover = true;
        break;
      }
    }

    if (!anyHover) {
      setCurrentHoverHeart(undefined);
    }
  };

  const onDragHeartStart = (e: DraggableEvent, data: DragCallbackData) => {};

  const clearAll = () => {
    const lastDisabled = soundSlots[soundSlots.length - 1].isDisabled;
    const newSoundSlots = soundSlots.map(() => ({ value: "" }));
    if (lastDisabled) {
      newSoundSlots.splice(newSoundSlots.length - 1, 1);
    }
    setAllState(newSoundSlots, []);
  };

  const clearSlot = (index: number) => {
    const newSoundSlots = [...soundSlots];

    if (newSoundSlots[index].relatedSoundIndex !== undefined) {
      if (
        newSoundSlots[index].relatedSoundIndex === newSoundSlots.length - 1 &&
        newSoundSlots[newSoundSlots[index].relatedSoundIndex!].isDisabled
      ) {
        newSoundSlots.splice(newSoundSlots[index].relatedSoundIndex!, 1);
      } else {
        newSoundSlots[newSoundSlots[index].relatedSoundIndex!] = { value: "" };
      }
    }

    newSoundSlots[index] = { value: "" };
    const newHeartSlots = [...heartSlots];
    const heartIndex = newHeartSlots.indexOf(index);
    newHeartSlots.splice(heartIndex, 1);

    setAllState(newSoundSlots, newHeartSlots);
  };

  const clearHeart = (index: number) => {
    console.log(index);
    const newHeartSlots = [...heartSlots];
    const heartIndex = newHeartSlots.indexOf(index);
    if (heartSlots[heartIndex] === index) {
      newHeartSlots.splice(heartIndex, 1);
      setHeartSlots(newHeartSlots);
    }
  };

  const isSlotHovered = (slotIndex: number) => {
    if (currentDragPhoneme !== undefined && currentHoverDropzone !== undefined) {
      let width = Math.floor(currentDragPhoneme.sounds.length);
      if (width > 1) {
        width += 1; // Account for the gap
      }
      let hoverSlotStart = currentHoverDropzone;
      let hoverSlotEnd = currentHoverDropzone + width - 1;
      return hoverSlotStart <= slotIndex && slotIndex <= hoverSlotEnd;
    }
    return false;
  };

  const adjustSlotCount = (adjust: number) => {
    const newSlots = soundSlots.map((slot, index) => {
      if (index === soundSlots.length - 1 && slot.isDisabled) {
        const { isDisabled, ...rest } = slot;

        return rest;
      }

      return slot;
    });

    for (let i = 0; i < Math.abs(adjust); i++) {
      if (adjust > 0) {
        if (newSlots.length < MaxSlots) {
          newSlots.push({ value: "" });
        }
      } else {
        if (newSlots.length > 1) {
          newSlots.pop();
        }
      }
    }

    setSoundSlots(newSlots);
  };

  const speakAloud = () => {
    let phrase = "";
    for (let sound of soundSlots) {
      if (sound.value === "") {
        phrase += " ";
      } else {
        phrase += sound.value;
      }
    }
    readAloud(phrase);
  };

  const isGroupDragged = (groupKey: string) =>
    currentDragPhoneme &&
    PhonemeGroups[groupKey].groups.some((key) =>
      Phonemes[key].phonemes.some((phoneme) => phoneme === currentDragPhoneme),
    );

  const groupsShowing = Object.keys(phonemesDisabled).filter(
    (key) => !phonemesDisabled[key].disabled,
  ).length;

  const getGroupSize = (mainGroupKey: string) => {
    if (groupsShowing >= 3 || groupsToShow.length < 3) {
      return lerp(
        PhonemeGroups[mainGroupKey].minMaxSize[1],
        PhonemeGroups[mainGroupKey].minMaxSize[0],
        1 - groupsToShow.length / 3,
      );
    } else {
      return PhonemeGroups[mainGroupKey].standardSize;
    }
  };

  const getTileSize = () => {
    if (groupsShowing > 3 || groupsToShow.length < 3) {
      return lerp(7.5, 4.5, groupsToShow.length / 3);
    } else {
      return 5.8;
    }
  };

  return (
    <OuterContainer>
      <UpperContainer>
        <SlotButtonControls style={{ zIndex: 10 }}>
          {heartParts && (
            <DragHeartContainer className="no-drag">
              <DragHeartContainerIcon>
                <ArrowsMove />
              </DragHeartContainerIcon>
              <InactiveHeart>
                <HeartFilled color="var(--danger)" />
              </InactiveHeart>
              <Draggable
                position={{ x: 0, y: 0 }}
                onStart={onDragHeartStart}
                onDrag={onDragHeart}
                onStop={onDragHeartStop}
              >
                <DragHeartInner>
                  <HeartFilled color="var(--danger)" />
                </DragHeartInner>
              </Draggable>
            </DragHeartContainer>
          )}
          <IconButtonInverted
            Icon={TextToSpeech}
            accentColor="var(--primary)"
            withShadow={false}
            onClick={speakAloud}
          />
        </SlotButtonControls>
        <SoundSlotContainer>
          {soundSlots.map((slot, index) => (
            <Col key={index}>
              {heartParts && (
                <HeartDropZone
                  ref={heartZoneRefs.current[index]}
                  showHeart={index === currentHoverHeart || heartSlots.includes(index)}
                  onClick={() => clearHeart(index)}
                  size={Math.min(70 / soundSlots.length, 20)}
                  className="no-drag"
                  isDisabled={Boolean(slot.isDisabled)}
                >
                  <HeartFilled color="var(--danger)" />
                </HeartDropZone>
              )}
              <SoundSlotElement
                ref={dropZoneRefs.current[index]}
                isHover={isSlotHovered(index)}
                size={Math.min(70 / soundSlots.length, 20)}
                className="no-drag"
                isFilled={slot.value !== ""}
                isDisabled={Boolean(slot.isDisabled)}
              >
                {slot.value}

                {slot.isDisabled ? null : (
                  <SlotClearButton
                    Icon={Remove}
                    accentColor="var(--danger)"
                    withShadow={false}
                    onClick={() => clearSlot(index)}
                  />
                )}
              </SoundSlotElement>
            </Col>
          ))}
        </SoundSlotContainer>
        <SlotButtonControls>
          <IconButtonInverted
            Icon={Add}
            accentColor="var(--primary)"
            withShadow={false}
            onClick={() => adjustSlotCount(1)}
          />
          <IconButtonInverted
            Icon={Remove}
            accentColor="var(--primary)"
            withShadow={false}
            onClick={() => adjustSlotCount(-1)}
          />
          <IconButtonInverted
            Icon={Reset}
            accentColor="var(--danger)"
            withShadow={false}
            onClick={clearAll}
          />
        </SlotButtonControls>
      </UpperContainer>
      <LowerContainer>
        {groupsToShow.length > 0 ? (
          groupsToShow.map((mainGroupKey, index) => (
            <PhonemeOuterGroup
              key={index}
              style={{
                flex: getGroupSize(mainGroupKey),
                zIndex: isGroupDragged(mainGroupKey) ? 10 : 0,
              }}
            >
              <PhonemeHeader>{mainGroupKey}</PhonemeHeader>
              <PhonemeGroup key={index}>
                {PhonemeGroups[mainGroupKey].groups
                  .filter((groupKey) => !phonemesDisabled[groupKey].disabled)
                  .map((key) =>
                    Phonemes[key].phonemes
                      .filter(
                        (phoneme) =>
                          !phonemesDisabled[key].disabledSounds.includes(phoneme.sounds.join("_")),
                      )
                      .map((phoneme, index) => (
                        <DragSoundContainer
                          key={index}
                          style={{
                            zIndex: phoneme === currentDragPhoneme ? 10 : 0,
                          }}
                        >
                          <InactiveDragSound size={getTileSize()} color={Phonemes[key].color}>
                            {phoneme.sounds.join("_")}
                          </InactiveDragSound>
                          <Draggable
                            position={{ x: 0, y: 0 }}
                            onStart={(e, d) => onDragPhonemeStart(e, d, phoneme)}
                            onDrag={onDragPhoneme}
                            onStop={onDragPhonemeStop}
                          >
                            <DragSound size={getTileSize()} color={Phonemes[key].color}>
                              {phoneme.sounds.join("_")}
                            </DragSound>
                          </Draggable>
                        </DragSoundContainer>
                      )),
                  )}
              </PhonemeGroup>
            </PhonemeOuterGroup>
          ))
        ) : (
          <NothingSelected>
            <EmptyText>
              Select some Phoneme groups from the
              <TextBtn onClick={onOpenSettings}>
                <WidgetSettings width={"1em"} height={"1em"} /> <b>Settings</b>
              </TextBtn>
              menu to get started
            </EmptyText>
          </NothingSelected>
        )}
      </LowerContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  width: 100%;
  height: 100%;
  padding: 10px;
  gap: 2cqh;
`;

const UpperContainer = styled(Row)`
  justify-content: space-between;
  align-items: center;
  height: 35cqh;
`;

const LowerContainer = styled(Row)`
  width: 100%;
  flex: 1;
  gap: 1cqw;
  justify-content: center;
`;

const PhonemeOuterGroup = styled(Col)`
  flex: 0.4;
`;

const PhonemeGroup = styled(Row).attrs({ className: "no-drag" })`
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 1cqw;
`;

const PhonemeHeader = styled.p`
  height: 5cqh;
  font-weight: 600;
  font-size: 2cqh;
  line-height: 1.3em;
`;

const DragSoundContainer = styled.div`
  position: relative;

  .react-draggable-dragging {
    box-shadow: 0 0 3px 0 black;
  }
`;

const DragSound = styled(Row)<{ color: string; size: number }>`
  width: ${(props) => props.size}cqw;
  height: ${(props) => props.size}cqw;
  border-radius: 5px;
  background-color: ${(props) => props.color};
  align-items: center;
  justify-content: center;
  color: black;
  font-size: ${(props) => props.size / 2.75}cqw;
  font-weight: 600;
`;

const InactiveDragSound = styled(Row)<{ color: string; size: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.color};
  border-radius: 5px;
  align-items: center;
  justify-content: center;
  color: black;
  filter: brightness(110%);
  font-size: ${(props) => props.size / 2.75}cqw;
  font-weight: 600;
`;

const SoundSlotContainer = styled(Row)`
  height: 100%;
  width: 70cqw;
  align-items: center;
  justify-content: center;
`;

const SlotClearButton = styled(IconButtonInverted)`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 2cqw;
  height: 2cqw;
  border-radius: 1px;

  ${StyledIconBase} {
    width: 30px;
    height: 30px;
  }
`;

const SoundSlotElement = styled(Row)<{
  isHover: boolean;
  size: number;
  isFilled: boolean;
  isDisabled: boolean;
}>`
  width: ${(props) => props.size}cqw;
  height: ${(props) => props.size}cqw;
  justify-content: center;
  align-items: center;
  border: ${(props) => (props.isDisabled ? "none" : "1px solid black")};
  font-size: ${(props) => props.size / 3}cqw;
  font-weight: bold;
  position: relative;
  background-color: ${(props) => {
    if (props.isDisabled) {
      return "transparent";
    }

    return props.isHover ? "rgba(0, 0, 0, 0.2)" : props.isFilled ? "#EAF0FF" : "white";
  }};
  color: ${(props) => (props.isDisabled ? "var(--gray)" : "var(--secondary)")};

  ${SlotClearButton} {
    display: none;
    padding: 0.5cqw;
  }

  &:hover {
    ${SlotClearButton} {
      display: flex;
    }
  }
`;

const SlotButtonControls = styled(Col)`
  flex: 0.2;
  gap: 1cqw;
  padding: 1cqw;
  justify-content: space-around;
  height: 100%;

  button {
    width: 7cqw;
    height: 7cqw;
    padding: 10px;
  }
`;

const HeartDropZone = styled(Row)<{
  showHeart: boolean;
  size: number;
  isDisabled: boolean;
}>`
  justify-content: center;
  align-items: center;
  height: ${(props) => props.size / 2}cqh;
  background-color: #efefef;
  border: 1px solid white;
  visibility: ${(props) => (props.isDisabled ? "hidden" : "visible")};

  svg {
    height: 60%;
    aspect-ratio: 1;
    display: ${(props) => (props.showHeart ? "inline-block" : "none")};
  }
`;

const DragHeartContainer = styled(Row)`
  width: 7cqw;
  height: 7cqw;
  position: relative;
  border-radius: 5px;
  background-color: #efefef;
  justify-content: center;
  align-items: center;

  svg {
    width: 50%;
    height: 50%;
  }
`;

const InactiveHeart = styled(Row)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  transform: translate(-25%, -25%);
`;

const DragHeartContainerIcon = styled(Row)`
  position: absolute;
  bottom: 0;
  right: 0;
  transform: translate(30%, 30%);
  width: 2.5cqw;
  height: 2.5cqw;
  background-color: #efefef;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
`;

const NothingSelected = styled(Row)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;

  p {
    font-size: 2cqw;
    font-weight: 600;
  }
`;

const DragHeartInner = styled(Row)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  z-index: 10;
  color: blue;
  cursor: pointer;
`;

const EmptyText = styled.p`
  display: flex;
`;

const TextBtn = styled.button`
  border: none;
  box-shadow: none;
  background-color: transparent;
  display: flex;
  color: var(--primary);
  align-items: center;
  padding: 0 5px;
  gap: 5px;
`;
